import { motion } from "framer-motion";
import { RevealOnScroll } from "./Reveal";
import {
  Mail, ClipboardCheck, Globe2, FileText, CheckCircle2,
  Wallet, PackageCheck, Ship, KeyRound,
} from "lucide-react";

const STEPS = [
  { n: "01", icon: Mail,            title: "Enquiry",              body: "You describe the vehicle you want — make, model, year, trim, budget or use-case. A single form or WhatsApp message is enough." },
  { n: "02", icon: ClipboardCheck,  title: "Specifications Confirmed", body: "We reply with a shortlist and confirm every specification, colour, condition and expected timing before anything is sourced." },
  { n: "03", icon: Globe2,          title: "Sourced Globally",     body: "Your vehicle is procured through Maklan's trusted international supplier network across Asia, Europe and the Gulf." },
  { n: "04", icon: FileText,        title: "Formal Quotation",     body: "A single, written quotation covering vehicle, shipping, duties and delivery — no surprises, no line-item negotiations." },
  { n: "05", icon: CheckCircle2,    title: "Client Approval",      body: "You review, question, and approve. Nothing moves without your written confirmation." },
  { n: "06", icon: Wallet,          title: "Payment on Terms",     body: "Payment is completed on the agreed schedule — deposit, balance, or milestone-based, depending on the transaction." },
  { n: "07", icon: PackageCheck,    title: "Preparation & Export", body: "Your vehicle is prepared at origin: inspected, cleaned, documented, and released for export." },
  { n: "08", icon: Ship,            title: "Shipping & Customs",   body: "Ocean freight, port clearance and duties are coordinated end-to-end by our logistics corridor." },
  { n: "09", icon: KeyRound,        title: "Delivered to You",     body: "Final inspection, registration where applicable, and delivery to your preferred address in Ghana." },
];

export default function HowItWorks() {
  return (
    <section data-testid="how-it-works" className="section-y-lg bg-navy border-t border-white/5">
      <div className="container-editorial">
        <div className="grid grid-cols-12 gap-8 md:gap-16 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-7">
            <motion.span
              initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }}
              viewport={{ once: true, amount: 0.6 }}
              transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
              className="block h-px bg-[#C5A059] w-14 mb-8 origin-left"
            />
            <div className="overline text-[#C5A059] mb-6">How Maklan Motors Works</div>
            <RevealOnScroll
              element="h2"
              className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight"
              lines={["Sourced for you.", "<em>Never off the shelf</em>."]}
            />
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-6">
            <p className="editorial-body">
              Maklan Motors is a pre-order vehicle sourcing house. Every car is
              procured against a written specification through our international
              supplier network — not held in a lot. Here is the nine-step corridor
              from enquiry to delivery.
            </p>
          </div>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-14 gap-y-16">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.1, delay: (i % 3) * 0.08, ease: [0.19, 1, 0.22, 1] }}
                className="pb-8 border-b border-white/10"
                data-testid={`hiw-step-${s.n}`}
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-display italic text-[#C5A059] text-4xl md:text-5xl leading-none">{s.n}</span>
                  <Icon size={20} className="text-white/50" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl mt-6 leading-tight">{s.title}</h3>
                <div className="w-10 h-px bg-white/15 my-5" />
                <p className="editorial-body max-w-sm">{s.body}</p>
              </motion.li>
            );
          })}
        </ol>

        <div className="mt-16 md:mt-20 text-center">
          <div className="text-[10px] tracking-[0.32em] uppercase text-white/40">
            Built on the Maklan Standard · Powered by the Maklan Global Network
          </div>
        </div>
      </div>
    </section>
  );
}
